/**
 * Helper function to get target origin that supports subdomains for postMessage
 * This resolves the issue where iframe is on a subdomain but landingUrl is on the main domain
 *
 * @param landingUrl - The landing URL from the direction object
 * @returns The appropriate target origin for postMessage
 */
export const getTargetOrigin = (landingUrl: string): string => {
  try {
    // First try to get the parent window's origin if available and secure
    if (window.parent !== window && document.referrer) {
      const referrerUrl = new URL(document.referrer);
      const landingUrlObj = new URL(landingUrl);

      // Check if the referrer is a subdomain of the landing domain
      const referrerDomain = referrerUrl.hostname;
      const landingDomain = landingUrlObj.hostname;

      // If referrer is the same domain or a subdomain, use the referrer origin
      if (referrerDomain === landingDomain || referrerDomain.endsWith('.' + landingDomain)) {
        return referrerUrl.origin;
      }
    }

    // Fallback to the original landing URL origin
    const url = new URL(landingUrl);
    return url.origin;
  } catch (error) {
    // Fallback to the original landingUrl if URL parsing fails
    return landingUrl;
  }
};

/**
 * Safe postMessage function that handles subdomain communication
 *
 * @param message - The message to send to the parent window
 * @param landingUrl - The landing URL from the direction object
 */
export const safePostMessage = (message: unknown, landingUrl?: string): void => {
  if (!landingUrl) {
    // If no landingUrl, use '*' as fallback (less secure but functional)
    parent.postMessage(message, '*');
    return;
  }

  const targetOrigin = getTargetOrigin(landingUrl);
  parent.postMessage(message, targetOrigin);
};
